<template>
  <div class="tu-scope page-section ucd">
    <div :class="containerClass">
      <!-- Locked: no pathway enrollment covers this school (or this program at this school). -->
      <div v-if="!isLoading && !checklistAllowed" class="tu-empty">
        <md-icon>lock</md-icon>
        <h4 class="tu-empty-title">This scorecard is locked</h4>
        <p class="tu-empty-text" v-if="!checklistAccess.enrolledHere">
          Enroll in a path that includes {{ selectedUni.name || 'this school' }} to unlock its scorecards.
        </p>
        <p class="tu-empty-text" v-else>
          Your path enrollment doesn't include this program at {{ selectedUni.name || 'this school' }}, so its
          scorecard isn't available yet.
        </p>
        <router-link :to="{ name: 'student-pathways' }" class="tu-btn tu-btn--primary tu-empty-cta">Browse paths</router-link>
      </div>

      <div v-else-if="isLoading" class="text-center py-5"><b-spinner /></div>

      <template v-else>
        <!-- Header: the program, then who offers it (same order as the program cards) -->
        <header class="ucd-head">
          <p class="ucd-kicker">Prepare your application</p>
          <span v-if="programAbbr" class="tu-credential-badge ucd-chip">{{ programAbbr }}</span>
          <h1 class="ucd-title">{{ programTitle }}</h1>
          <div class="ucd-offered">
            <span class="ucd-offered__label">Offered by</span>
            <div class="ucd-offered__row">
              <span class="ucd-logo" aria-hidden="true">
                <img v-if="selectedUni.small_logo_url" :src="selectedUni.small_logo_url" alt="" />
                <span v-else>{{ (selectedUni.name || '?').charAt(0) }}</span>
              </span>
              <div class="ucd-offered__copy">
                <strong>{{ selectedUni.name }}</strong>
                <p v-if="uniLocation"><tu-ui-icon name="location" :size="13" />{{ uniLocation }}</p>
              </div>
            </div>
          </div>
        </header>

        <div class="ucd-layout">
          <!-- Rail: the score, the two actions, the dates -->
          <aside class="ucd-rail">
            <div class="ucd-card ucd-score" v-if="scorecard">
              <span class="ucd-card__label">Your score</span>
              <div class="ucd-score__num">
                <b-skeleton v-if="isScoreLoading" width="90px" height="34px" />
                <template v-else><b>{{ $n(scorecard.earned) }}</b><span>of {{ $n(scorecard.total) }}</span></template>
              </div>
              <div class="ucd-score__track" role="progressbar" :aria-valuenow="scorecard.earned" aria-valuemin="0" :aria-valuemax="scorecard.total">
                <div class="ucd-score__fill" :style="{ width: `${scorecard.earnedPct}%` }" />
                <div v-if="scorecard.floor" class="ucd-score__floor" :style="{ left: `${scorecard.floorPct}%` }" />
              </div>
              <p v-if="scorecard.floor" class="ucd-score__note"><i class="ucd-score__dot" />Floor {{ $n(scorecard.floor) }} points: the steps that match this program's required prerequisites.</p>
            </div>

            <div class="ucd-actions">
              <a v-if="applyHref" :href="applyHref" target="_blank" rel="noopener" class="ucd-btn ucd-btn--solid">Apply now<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14m-5-5 5 5-5 5"/></svg></a>
              <button v-else type="button" class="ucd-btn ucd-btn--solid" disabled aria-disabled="true" title="Applications open soon">Apply now</button>
              <button type="button" class="ucd-btn ucd-btn--ghost" :disabled="!admissionContacts.length" @click="contactsOpen = !contactsOpen">
                Connect with admissions<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>
              </button>
              <ul v-if="contactsOpen && admissionContacts.length" class="ucd-contacts">
                <li v-for="contact in admissionContacts" :key="`ac-${contact.id}`">
                  <b>{{ contact.name || 'Admissions' }}</b>
                  <span v-if="contact.title">{{ contact.title }}</span>
                  <a v-if="contact.email" :href="`mailto:${contact.email}`">{{ contact.email }}</a>
                  <a v-if="contact.phone" :href="`tel:${contact.phone}`">{{ contact.phone }}</a>
                </li>
              </ul>
            </div>

            <dl class="ucd-card ucd-dates">
              <div><dt>{{ admission.label }}</dt><dd>{{ admission.value }}</dd></div>
              <div v-if="nextStartLabel"><dt>Next start</dt><dd>{{ nextStartLabel }}</dd></div>
              <div v-if="offering && offering.admission_cycle === 'rolling'"><dt>Admissions</dt><dd>Rolling</dd></div>
            </dl>
          </aside>

          <!-- Main: two tabs, the same underline tabs as the public page -->
          <div class="ucd-main">
            <nav class="ucd-tabs" aria-label="Program sections">
              <button type="button" class="ucd-tab" :class="{ 'is-active': tab === 'prereqs' }" role="tab" :aria-selected="tab === 'prereqs' ? 'true' : 'false'" @click="tab = 'prereqs'">
                Prereqs <span class="ucd-count">{{ prerequisites.length }}</span>
              </button>
              <button type="button" class="ucd-tab" :class="{ 'is-active': tab === 'scorecard' }" role="tab" :aria-selected="tab === 'scorecard' ? 'true' : 'false'" @click="tab = 'scorecard'">
                Scorecard
              </button>
            </nav>

            <!-- PREREQS -->
            <section v-show="tab === 'prereqs'" class="ucd-panel">
              <p class="ucd-lead">What {{ selectedUni.name || 'this school' }} asks for before you apply.<span v-if="prerequisitesVerifiedOn"> Last verified {{ prerequisitesVerifiedOn }}.</span></p>
              <div v-if="prerequisites.length" class="ucd-filter" role="group" aria-label="Filter prerequisites by level">
                <button v-for="f in prqFilters" :key="f.key" type="button" class="ucd-seg" :class="{ 'is-on': prqLevel === f.key }" :aria-pressed="prqLevel === f.key ? 'true' : 'false'" @click="prqLevel = f.key">
                  <span>{{ f.label }}</span><span class="ucd-seg__n">{{ f.n }}</span>
                </button>
              </div>
              <p v-if="!prerequisites.length" class="ucd-none">No prerequisites listed for this program yet.</p>
              <p v-else-if="!prereqGroups.length" class="ucd-none">No {{ prqLabel.toLowerCase() }} prerequisites for this program.</p>
              <details v-for="g in prereqGroups" :key="g.key" class="tu-prq-group" open>
                <summary class="tu-prq-group__sum">
                  <span class="tu-prq-group__ico" aria-hidden="true"><tu-ui-icon :name="g.icon" :size="20" /></span>
                  <h3 class="tu-prq-group__title">{{ g.label }}</h3>
                  <span class="tu-prq-group__count">{{ g.items.length }}</span>
                  <svg class="tu-prq-group__chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>
                </summary>
                <ul class="ucd-prq-grid">
                  <li v-for="item in g.items" :key="item.id">
                    <button type="button" class="ucd-prq-item" :aria-label="`${item.name}: details`" @click="prereqOpen = { row: item, group: g.key }">
                      <span class="ucd-prq-item__text">
                        <b>{{ item.name }}</b>
                        <small>{{ prereqSub(item) }}</small>
                      </span>
                      <span v-if="item.points" class="ucd-prq-item__pts">+{{ item.points }}</span>
                      <svg class="ucd-prq-item__go" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 5 7 7-7 7"/></svg>
                    </button>
                  </li>
                </ul>
              </details>
              <p v-if="showsExamCredit" class="ucd-none">Credit by exam rules vary. Confirm CLEP or DSST credit with the school before you test.</p>
            </section>

            <!-- SCORECARD -->
            <section v-show="tab === 'scorecard'" class="ucd-panel">
              <p class="ucd-lead">Every step adds points to your application. Complete them here and your score updates.</p>
              <div v-if="!scoreSteps.length" class="ucd-none">No scorecard items available for this selection.</div>
              <div v-else class="ucd-steps">
                <!-- Resume: one group that reads as one step until you open it -->
                <details v-if="resumeSteps.length" class="ucd-group">
                  <summary class="ucd-group__sum">
                    <md-icon class="ucd-group__ico">description</md-icon>
                    <div class="ucd-group__copy">
                      <strong>Resume</strong>
                      <p>Education, work, volunteer experience, skills, and your latest title and location.</p>
                    </div>
                    <span class="ucd-group__pts" :class="{ 'is-on': resumeTotals.earned > 0 }">
                      <b-skeleton v-if="isScoreLoading" width="56px" height="18px" />
                      <template v-else>{{ resumeTotals.earned }} / {{ resumeTotals.total }}</template>
                    </span>
                    <svg class="ucd-group__chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>
                  </summary>
                  <div class="ucd-group__body">
                    <div v-for="(guide, index) in resumeSteps" :key="`resume-${guide.abbr}-${index}`" class="ucd-row">
                      <care-campus-checklist-row
                        :title="guide.title"
                        :description="guide.description"
                        :isLoading="isScoreLoading"
                        :status="stepStatus"
                        :redirect-to="guide.redirectTo"
                        :icon="guide.icon"
                        :abbr="guide.abbr"
                        :btn-text="guide.btnText"
                        :selectedUni="selectedUni"
                        :redirect-query="getRedirectQuery({ abbr: guide.abbr, programId: stepProgramId, checklistConfigId: guide.id })"
                      />
                    </div>
                  </div>
                </details>
                <div v-for="(guide, index) in otherSteps" :key="`step-${guide.abbr}-${index}`" class="ucd-row ucd-row--card">
                  <care-campus-checklist-row
                    :title="guide.title"
                    :description="guide.description"
                    :isLoading="isScoreLoading"
                    :status="stepStatus"
                    :redirect-to="guide.redirectTo"
                    :icon="guide.icon"
                    :abbr="guide.abbr"
                    :btn-text="guide.btnText"
                    :selectedUni="selectedUni"
                    :redirect-query="getRedirectQuery({ abbr: guide.abbr, programId: stepProgramId, checklistConfigId: guide.id })"
                  />
                </div>
              </div>
            </section>
          </div>
        </div>
      </template>
    </div>

    <tu-prereq-modal v-if="prereqOpen" :row="prereqOpen.row" :group="prereqOpen.group" :program-label="programAbbr || programTitle" @close="prereqOpen = null" />
  </div>
</template>
<script>
import { get, truncate } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import Page from '../../../components/Page.vue';
import CareCampusChecklistRow from './CareCampusChecklistRow.vue';
import { DASHBOARD_TYPES } from '@/common/constants';
import DefaultAvatar from '@/assets/images/logos/logo_icon.png';
import { enrollmentAccessForUniversity } from '@/common/tuPathway';
import { programAdmission, nextStart } from '@/common/tuProgram';
import TuUiIcon from '@/tu/components/UiIcon.vue';
import TuPrereqModal, { PREREQ_GROUP_ICONS } from '@/tu/components/PrereqModal.vue';

// Prerequisite groups in the order the public path page shows them.
const PREREQ_GROUPS = [
  { key: 'college_course', label: 'College courses' },
  { key: 'entrance_exam', label: 'Entrance exams' },
  { key: 'prep_course', label: 'Prep courses' },
  { key: 'certification', label: 'Certifications' },
  { key: 'experience', label: 'Experience' },
  { key: 'compliance', label: 'Compliance' },
  { key: 'general', label: 'Other requirements' },
];
// Scorecard steps that live on the resume; they fold into one row.
const RESUME_STEPS = ['education_resume', 'work_experience_resume', 'volunteer_experience_resume', 'skills_resume', 'latest_title_location_resume'];
function prqItemLevel(item) {
  return ['required', 'highly_recommended', 'recommended'].includes(item.level) ? item.level : 'required';
}

// The checklist step a prerequisite scores under; mirrors TransitionBackend/ops/checklist_points_from_prereqs.py.
// Compliance items, CPR and a high school diploma or GED are not scored.
function prerequisiteStep(name, type) {
  const n = String(name || '').toLowerCase();
  if (type === 'college_course') return 'add_new_courses';
  if (type === 'entrance_exam') return 'add_admissions_assessments';
  if (type === 'prep_course') return 'add_prep_courses';
  if (type === 'certification') {
    if (/cpr|\bbls\b|basic life|first aid/.test(n)) return null;
    if (/\blpn\b|\blvn\b|\brn\b|license/.test(n)) return 'add_licenses';
    return 'add_certifications';
  }
  if (type === 'experience') return /volunteer|observ|shadow/.test(n) ? 'volunteer_experience_resume' : 'work_experience_resume';
  if (type === 'general') {
    if (/reference|recommendation/.test(n)) return 'add_letters_of_recommendation';
    if (/high school|diploma|\bged\b|hiset|equivalen/.test(n) && !/associate|bachelor|college degree|\bdegree in\b/.test(n)) {
      return null;
    }
    if (/degree|transcript/.test(n)) return 'add_degrees';
    if (/interview/.test(n)) return 'practice_skill_simulations';
    if (/essay|statement|resume/.test(n)) return 'skills_resume';
    if (/activit|volunteer|community/.test(n)) return 'add_activities';
  }
  return null;
}

export default {
  extends: Page,
  name: 'CareCampusUniChecklistDetail',
  components: { CareCampusChecklistRow, TuUiIcon, TuPrereqModal },
  data() {
    return {
      title: 'Scorecard',
      completeHeadTitle: 'Scorecard - Transition',
      DefaultAvatar,
      isLoading: true,
      isScoreLoading: true,
      orgStatuses: { general: {}, programs: [] },
      DASHBOARD_TYPES,
      selectedUni: {},
      admissionContacts: [],
      prerequisites: [],
      myEnrollments: [],
      tab: 'prereqs',
      prqLevel: 'all',
      prereqOpen: null,
      contactsOpen: false,
      offering: null,
      defaultChecklistActions: [
        {
          title: 'Take a Career Assessment',
          abbr: 'career_assessment',
          btnText: 'Complete',
          description: 'Complete a Basic Assessment to earn 1 point or an Advanced Assessment to earn 5.',
          enable: true,
          icon: 'assessment',
          redirectTo: '',
        },
        {
          title: 'Add Education to Resume',
          abbr: 'education_resume',
          btnText: 'Complete',
          description: 'Earn 1 point for every education entry added to your resume.',
          enable: true,
          icon: 'description',
          redirectTo: 'std-manage-resume',
        },
        {
          title: 'Add Work Experience to Resume',
          abbr: 'work_experience_resume',
          btnText: 'Complete',
          description: 'Earn 1 point for each work experience entry you include in your resume.',
          enable: true,
          icon: 'description',
          redirectTo: 'std-manage-resume',
        },
        {
          title: 'Add Volunteer Work Experience to Resume',
          abbr: 'volunteer_experience_resume',
          btnText: 'Complete',
          description: 'Earn 1 point for each volunteer work experience entry you include in your resume.',
          enable: true,
          icon: 'description',
          redirectTo: 'std-manage-resume',
        },
        {
          title: 'Add Skills to Resume',
          abbr: 'skills_resume',
          btnText: 'Complete',
          description: 'Earn 1 point for each skill entry to your resume.',
          enable: true,
          icon: 'description',
          redirectTo: 'std-manage-resume',
        },
        {
          title: 'Add Latest Title and Location to Resume',
          abbr: 'latest_title_location_resume',
          btnText: 'Complete',
          description: 'Earn up to 2 points for adding your current job title and location.',
          enable: true,
          icon: 'description',
          redirectTo: 'std-manage-resume',
        },
        {
          title: 'Interview',
          abbr: 'practice_skill_simulations',
          btnText: 'Complete',
          description: 'Earn 3 points for completing a real-world skill interview.',
          enable: true,
          icon: 'mic',
          redirectTo: 'student-simulations-listing',
        },
        {
          title: 'Add Certifications',
          abbr: 'add_certifications',
          btnText: 'Complete',
          description: 'Earn 5 points for each certification you upload to your profile.',
          enable: true,
          icon: 'assignment_turned_in',
          redirectTo: 'add-certificate',
        },
        {
          title: 'Add Courses',
          abbr: 'add_new_courses',
          btnText: 'Complete',
          description: 'Earn 3 points for every course entry you include to demonstrate academic progress.',
          enable: true,
          icon: 'local_library',
          redirectTo: 'add-course',
        },
      ],
    };
  },
  watch: {
    '$route.fullPath'() {
      if (get(this.selectedUni, 'id') && this.checklistAllowed) {
        this.fetchDashboardScore();
      }
      if (get(this.selectedUni, 'id')) {
        this.fetchPrerequisites();
        this.fetchOffering();
      }
    },
  },
  computed: {
    ...mapGetters('auth', ['getLoggedInUser', 'getLoggedInUserDashboard']),
    ...mapGetters('uni', ['getSelectedStudentUniversity']),
    /** Program scope only: earned and possible points across the program's steps, and the floor from required prerequisites. */
    scorecard() {
      const group = this.selectedScope === 'program' ? this.visibleProgramChecklistGroups[0] : null;
      if (!group) return null;
      const steps = group.checklist || [];
      const total = steps.reduce((n, step) => n + (step.points || 0), 0);
      if (!total) return null;
      const status = this.getProgramStatus(group.program_id);
      const earned = steps.reduce((n, step) => n + (Number(get(status, `${step.abbr}.earned`)) || 0), 0);
      const requiredSteps = new Set(
        this.prerequisites
          .filter(prereq => prereq.level === 'required')
          .map(prereq => prerequisiteStep(prereq.name, prereq.type))
          .filter(Boolean),
      );
      const floor = steps.filter(step => requiredSteps.has(step.abbr)).reduce((n, step) => n + (step.points || 0), 0);
      const pct = n => Math.min(100, Math.round((n / total) * 100));
      return { total, earned, floor, earnedPct: pct(earned), floorPct: pct(floor) };
    },
    programGroup() {
      return this.selectedScope === 'program' ? this.visibleProgramChecklistGroups[0] || null : null;
    },
    programAbbr() {
      const uop = (get(this.selectedUni, 'offered_programs') || []).find(o => Number(get(o, 'program.id')) === this.selectedProgramId);
      return get(uop, 'program.abbr') || get(this.offering, 'program.abbr') || '';
    },
    programTitle() {
      if (this.selectedScope === 'general') return 'General scorecard';
      return get(this.programGroup, 'program_title') || get(this.offering, 'program.title') || 'Program';
    },
    uniLocation() {
      return [get(this.selectedUni, 'city'), get(this.selectedUni, 'state')].filter(Boolean).join(', ');
    },
    admission() {
      return programAdmission(this.offering || {});
    },
    nextStartLabel() {
      return nextStart(this.offering || {});
    },
    applyHref() {
      const o = this.offering || {};
      return o.application_portal || o.enroll_link || '';
    },
    prqFilters() {
      const n = key => this.prerequisites.filter(i => prqItemLevel(i) === key).length;
      return [
        { key: 'all', label: 'All', n: this.prerequisites.length },
        { key: 'required', label: 'Required', n: n('required') },
        { key: 'highly_recommended', label: 'Highly recommended', n: n('highly_recommended') },
        { key: 'recommended', label: 'Recommended', n: n('recommended') },
      ].filter(f => f.key === 'all' || f.n);
    },
    prqLabel() {
      return (this.prqFilters.find(f => f.key === this.prqLevel) || {}).label || 'All';
    },
    prereqGroups() {
      const keep = i => this.prqLevel === 'all' || prqItemLevel(i) === this.prqLevel;
      return PREREQ_GROUPS.map(g => ({
        ...g,
        icon: PREREQ_GROUP_ICONS[g.key] || 'verify',
        items: this.prerequisites.filter(i => (i.type || 'general') === g.key && keep(i)),
      })).filter(g => g.items.length);
    },
    scoreSteps() {
      if (this.selectedScope === 'general') return this.generalChecklistDashboard;
      return get(this.programGroup, 'checklist') || [];
    },
    resumeSteps() {
      return this.scoreSteps.filter(step => RESUME_STEPS.includes(step.abbr));
    },
    otherSteps() {
      return this.scoreSteps.filter(step => !RESUME_STEPS.includes(step.abbr));
    },
    stepProgramId() {
      return this.selectedScope === 'general' ? null : get(this.programGroup, 'program_id') || this.selectedProgramId;
    },
    stepStatus() {
      return this.selectedScope === 'general' ? this.getGeneralStatus() : this.getProgramStatus(this.stepProgramId);
    },
    resumeTotals() {
      const status = this.stepStatus || {};
      return this.resumeSteps.reduce(
        (acc, step) => ({
          earned: acc.earned + (Number(get(status, `${step.abbr}.earned`)) || 0),
          total: acc.total + (Number(get(status, `${step.abbr}.total`)) || step.points || 0),
        }),
        { earned: 0, total: 0 },
      );
    },
    showsExamCredit() {
      return this.prerequisites.some(prereq => prereq.accepts_clep || prereq.accepts_dsst || prereq.accepts_ace);
    },
    prerequisitesVerifiedOn() {
      const times = this.prerequisites
        .map(prereq => new Date(prereq.verified_at || prereq.updated_at).getTime())
        .filter(time => !isNaN(time));
      return times.length ? this.formatVerifiedDate(Math.max(...times)) : '';
    },
    selectedProgramId() {
      const programId = Number(this.$route.query.program_id);
      return Number.isFinite(programId) && programId > 0 ? programId : null;
    },
    selectedScope() {
      const scope = String(this.$route.query.scope || '').toLowerCase();
      if (['general', 'program'].includes(scope)) {
        return scope;
      }
      return this.selectedProgramId ? 'program' : 'general';
    },
    showGeneralChecklist() {
      return this.selectedScope === 'general';
    },
    checklistAccess() {
      return enrollmentAccessForUniversity(this.myEnrollments, get(this.selectedUni, 'id'));
    },
    // General checklist opens with any enrollment at this school; a program checklist
    // only when the enrollment ties that program to this school.
    checklistAllowed() {
      if (!this.checklistAccess.enrolledHere) return false;
      if (this.selectedScope !== 'program') return true;
      if (!this.selectedProgramId) return false;
      return this.checklistAccess.programIds.has(this.selectedProgramId);
    },
    generalChecklistDashboard() {
      const checklist = get(this.selectedUni, 'general_checklist', []);
      if (Array.isArray(checklist) && checklist.length) {
        return this.normalizeChecklist(checklist);
      }
      return this.defaultChecklistActions;
    },
    programChecklistGroups() {
      const groups = get(this.selectedUni, 'program_checklists', []);
      if (!Array.isArray(groups) || !groups.length) {
        return [];
      }
      return groups
        .map(group => ({
          ...group,
          checklist: this.normalizeChecklist(group.checklist || [], group.program_id),
        }))
        .filter(group => group.checklist.length);
    },
    visibleProgramChecklistGroups() {
      if (this.selectedScope !== 'program') {
        return [];
      }
      if (this.selectedProgramId) {
        const selected = this.programChecklistGroups.find(
          group => Number(group.program_id) === this.selectedProgramId,
        );
        return selected ? [selected] : [];
      }
      return this.programChecklistGroups;
    },
    detailTitle() {
      const uniName = this.selectedUni.name || 'University';
      if (this.selectedScope === 'general') {
        return `${uniName} · General Scorecard`;
      }
      const programTitle = get(this.visibleProgramChecklistGroups, '0.program_title', 'Program Scorecard');
      return `${uniName} · ${programTitle}`;
    },
    admissionContactGroups() {
      const contacts = this.admissionContacts || [];
      if (!contacts.length) return [];
      if (this.selectedScope === 'program') {
        return [{ key: 'program', program_title: '', contacts }];
      }
      const grouped = {};
      contacts.forEach(contact => {
        const key = contact.program_id || 'university';
        if (!grouped[key]) {
          grouped[key] = {
            key: String(key),
            program_title: contact.program_title || '',
            contacts: [],
          };
        }
        grouped[key].contacts.push(contact);
      });
      return Object.values(grouped);
    },
  },
  methods: {
    ...mapActions('student', ['getUniversityChecklistScore']),
    ...mapActions('uni', ['getUniFromSlug', 'getAllUniversities', 'getAllAdmissionContacts', 'getAllPrerequisites', 'getAllUniversitiesByState']),
    ...mapActions('pathways', ['getMyPathwayEnrollments']),
    get,

    async fetchMyEnrollments() {
      try {
        const response = await this.getMyPathwayEnrollments({ limit: 200 });
        this.myEnrollments = get(response, 'data.results', []) || [];
      } catch (_err) {
        this.myEnrollments = [];
      }
    },

    goBack() {
      this.$router.push({
        name: 'std-cc-uni-checklist',
        params: { slug: this.$route.params.slug },
        query: { tab: 'checklist' },
      });
    },

    normalizeChecklist(checklist = [], fallbackProgramId = null) {
      return (checklist || [])
        .filter(item => item && item.is_enabled !== false)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
        .map(item => ({
          id: item.id || item.pk || item.checklist_config_id || item.config_id || null,
          title: item.title,
          description: item.description,
          abbr: item.abbr,
          btnText: item.btn_text || 'Complete',
          icon: item.icon,
          redirectTo: item.redirect_to || '',
          points: Number(item.points) || 0,
          program_id: item.program || fallbackProgramId || null,
        }));
    },

    getGeneralStatus() {
      return get(this.orgStatuses, 'general', {});
    },

    getProgramStatus(programId) {
      return (
        (get(this.orgStatuses, 'programs', []) || []).find(
          program => Number(program.program_id) === Number(programId),
        )?.scores || {}
      );
    },

    mapChecklistScoreToStatuses(scoreResp = {}) {
      const checklist = get(scoreResp, 'checklist', []);
      if (Array.isArray(checklist) && checklist.length) {
        const general = {};
        const programScoreMap = {};
        checklist.forEach(item => {
          if (!item || !item.abbr) return;
          const statusEntry = {
            earned: Number(item.earned) || 0,
            total: Number(item.total) || 0,
          };
          const programId = Number(item.program_id);
          if (Number.isFinite(programId) && programId > 0) {
            if (!programScoreMap[programId]) programScoreMap[programId] = {};
            programScoreMap[programId][item.abbr] = statusEntry;
          } else {
            general[item.abbr] = statusEntry;
          }
        });
        return {
          general,
          programs: Object.keys(programScoreMap).map(programId => ({
            program_id: Number(programId),
            scores: programScoreMap[programId],
          })),
        };
      }
      return {
        general: get(scoreResp, 'general', {}),
        programs: get(scoreResp, 'programs', []),
      };
    },

    isProgramScopedAction(abbr) {
      return [
        'add_certifications',
        'add_new_courses',
        'add_letters_of_recommendation',
        'add_activities',
        'add_admissions_assessments',
        'add_admission_assessments',
        'add_licenses',
        'add_degrees',
        'add_prep_courses',
      ].includes(abbr);
    },

    getRedirectQuery({ abbr, programId, checklistConfigId }) {
      const uniId = get(this.selectedUni, 'id');
      const query = { ...(uniId && { uniId }) };
      if (this.isProgramScopedAction(abbr) && programId) {
        query.program_id = programId;
        if (checklistConfigId) {
          query.checklist_config_id = checklistConfigId;
        }
      }
      return Object.keys(query).length ? query : null;
    },

    mergeChecklistConfig(university = {}, configuredUniversity = {}) {
      const baseUniversity =
        university && Object.keys(university).length ? university : configuredUniversity || {};
      if (!configuredUniversity || !Object.keys(configuredUniversity).length) {
        return baseUniversity;
      }
      return {
        ...configuredUniversity,
        ...baseUniversity,
        ...(configuredUniversity.general_checklist && {
          general_checklist: configuredUniversity.general_checklist,
        }),
        ...(configuredUniversity.program_checklists && {
          program_checklists: configuredUniversity.program_checklists,
        }),
      };
    },

    async fetchChecklistConfigBySlug(slug) {
      try {
        const response = await this.getAllUniversities({ slug, limit: 1, offset: 0 });
        const configuredUniversity = get(response, 'data.results.0', null);
        if (configuredUniversity) {
          this.selectedUni = this.mergeChecklistConfig(this.selectedUni, configuredUniversity);
        }
      } catch (_err) {
        return null;
      }
    },

    async fetchDashboardScore() {
      this.isScoreLoading = true;
      if (!get(this.selectedUni, 'id')) {
        this.orgStatuses = { general: {}, programs: [] };
        this.isScoreLoading = false;
        return;
      }
      try {
        const scoreParams = {
          university_id: this.selectedUni.id,
          scope: this.selectedScope,
          ...(this.selectedScope === 'program' && this.selectedProgramId
            ? { program_id: this.selectedProgramId }
            : {}),
        };
        const resp = await this.getUniversityChecklistScore(scoreParams);
        const uniScoreData = get(resp, 'data.data') || get(resp, 'data') || {};
        this.orgStatuses = this.mapChecklistScoreToStatuses(uniScoreData);
      } catch (err) {
        this.makeToast({
          variant: 'danger',
          msg:
            get(err, 'response.data.message') ||
            get(err, 'response.data.error') ||
            this.$t('generalMsgs.genErrorMsg'),
        });
      }
      this.isScoreLoading = false;
    },

    async fetchUniversity() {
      this.isLoading = true;
      const uniSlug = this.$route.params.slug;
      if (uniSlug) {
        const response = await this.getUniFromSlug(uniSlug);
        let university =
          get(response, 'data.data') ||
          get(response, 'data') ||
          get(response, 'results.0') ||
          get(response, 'data.results.0') ||
          get(response, 'university') ||
          response ||
          {};
        if (Array.isArray(university)) {
          university = university[0] || {};
        }
        this.selectedUni = this.mergeChecklistConfig(
          university,
          get(this.getSelectedStudentUniversity, 'slug') === uniSlug ? this.getSelectedStudentUniversity : null,
        );
        await this.fetchChecklistConfigBySlug(uniSlug);
        await this.fetchMyEnrollments();
        if (this.checklistAllowed) {
          await this.fetchDashboardScore();
          await Promise.all([this.fetchAdmissionContacts(), this.fetchPrerequisites(), this.fetchOffering()]);
        }
        this.completeHeadTitle = `${truncate(this.selectedUni.name)} Scorecard - Transition`;
      }
      this.isLoading = false;
    },

    async fetchPrerequisites() {
      const uniId = get(this.selectedUni, 'id');
      if (!uniId || this.selectedScope !== 'program' || !this.selectedProgramId) {
        this.prerequisites = [];
        return;
      }
      try {
        const res = await this.getAllPrerequisites({
          uni_offered_program__university: uniId,
          uni_offered_program__program: this.selectedProgramId,
          ordering: 'display_order',
          limit: 200,
        });
        const results = get(res, 'data.results') || get(res, 'results') || get(res, 'data') || [];
        this.prerequisites = Array.isArray(results) ? results : [];
      } catch (_err) {
        this.prerequisites = [];
      }
    },

    /** One line under a prerequisite: level, then the detail the school set (grade, score, credits...). */
    prereqSub(item) {
      const parts = [];
      if (item.level_label) parts.push(item.level_label);
      else if (item.level) parts.push(String(item.level).replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()));
      const details = this.prerequisiteDetails(item);
      if (details) parts.push(details);
      if (item.choice_group) parts.push(`One of: ${item.choice_group}`);
      return parts.join(' · ');
    },

    /** The directory row for this program at this school: deadline, admission facts, apply link. */
    async fetchOffering() {
      const uniId = get(this.selectedUni, 'id');
      if (!uniId || this.selectedScope !== 'program' || !this.selectedProgramId) {
        this.offering = null;
        return;
      }
      try {
        const res = await this.getAllUniversitiesByState({
          program_id: this.selectedProgramId, basic: true, limit: 5, search: get(this.selectedUni, 'name', ''),
        });
        const payload = (res && res.data !== undefined ? res.data : res) || {};
        const rows = Array.isArray(payload) ? payload : payload.results || [];
        const row = rows.find(r => Number(r.id) === Number(uniId));
        this.offering = (row && row.program_offering) || null;
      } catch (_err) {
        this.offering = null;
      }
    },

    prerequisiteIcon(type) {
      const icons = {
        college_course: 'local_library',
        prep_course: 'local_library',
        entrance_exam: 'assessment',
        certification: 'assignment_turned_in',
        experience: 'person',
      };
      return icons[type] || 'description';
    },

    formatVerifiedDate(value) {
      const date = value ? new Date(value) : null;
      if (!date || isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },

    prerequisiteDetails(prereq) {
      const parts = [];
      if (prereq.min_grade) parts.push(`Grade ${prereq.min_grade} or higher`);
      if (prereq.min_score !== null && prereq.min_score !== undefined) {
        parts.push(`Score ${prereq.min_score}${prereq.score_label ? ` (${prereq.score_label})` : ''} or higher`);
      }
      if (prereq.credits) parts.push(`${prereq.credits} credits${prereq.has_lab ? ' with lab' : ''}`);
      if (prereq.recency_years) parts.push(`Completed within ${prereq.recency_years} years`);
      if (prereq.due_by_label) parts.push(prereq.due_by_label);
      const accepted = [
        prereq.accepts_ace && 'ACE',
        prereq.accepts_clep && 'CLEP',
        prereq.accepts_dsst && 'DSST',
      ].filter(Boolean);
      if (accepted.length) parts.push(`Accepts ${accepted.join(', ')}`);
      if (prereq.accepts_online_lab === false) parts.push('In person lab required');
      return parts.join(' · ');
    },

    async fetchAdmissionContacts() {
      const uniId = get(this.selectedUni, 'id');
      if (!uniId) {
        this.admissionContacts = [];
        return;
      }
      try {
        const params = {
          uni_offered_program__university: uniId,
          ordering: 'name',
          limit: 100,
        };
        if (this.selectedScope === 'program' && this.selectedProgramId) {
          params.uni_offered_program__program = this.selectedProgramId;
        }
        const res = await this.getAllAdmissionContacts(params);
        const results = get(res, 'data.results') || get(res, 'results') || get(res, 'data') || [];
        this.admissionContacts = Array.isArray(results) ? results : [];
      } catch (_err) {
        this.admissionContacts = [];
      }
    },
  },

  async mounted() {
    this.fetchUniversity();
  },
};
</script>

<style lang="scss" scoped>
/* Program page: the program page of the public site, with the student's score and actions in a rail. */
.ucd-head { display: flex; flex-direction: column; align-items: flex-start; gap: 10px; margin: 4px 0 24px; }
.ucd-kicker { margin: 0; font-size: 12px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: var(--tu-blue); }
.ucd-chip { font-size: 12px; padding: 5px 10px; }
.ucd-title { margin: 0; font-family: var(--tu-font-display); font-weight: 800; font-size: clamp(28px, 3.2vw, 38px); letter-spacing: -0.025em; line-height: 1.1; color: var(--tu-ink); }
.ucd-offered { display: flex; flex-direction: column; gap: 6px; margin-top: 4px; }
.ucd-offered__label { font-size: 11px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: var(--tu-ink-mute); }
.ucd-offered__row { display: flex; align-items: center; gap: 12px; }
.ucd-logo { display: grid; place-items: center; flex: 0 0 44px; width: 44px; height: 44px; border-radius: 10px; background: #fff; box-shadow: inset 0 0 0 1px var(--tu-line); overflow: hidden; font-weight: 800; color: var(--tu-blue-deep); img { width: 100%; height: 100%; object-fit: contain; padding: 4px; box-sizing: border-box; } }
.ucd-offered__copy { min-width: 0; strong { display: block; font-size: 16px; font-weight: 700; color: var(--tu-ink); } p { display: flex; align-items: center; gap: 4px; margin: 2px 0 0; font-size: 13px; color: var(--tu-ink-soft); } }

.ucd-layout { display: grid; grid-template-columns: 320px minmax(0, 1fr); gap: 32px; align-items: start; }
.ucd-rail { display: flex; flex-direction: column; gap: 16px; position: sticky; top: 88px; }
.ucd-card { padding: 20px; border-radius: 16px; background: var(--tu-surface); border: 1px solid var(--tu-line); }
.ucd-card__label { font-size: 13px; font-weight: 600; color: var(--tu-ink-soft); }
.ucd-score__num { display: flex; align-items: baseline; gap: 8px; margin: 6px 0 12px; b { font-family: var(--tu-font-display); font-weight: 800; font-size: 40px; line-height: 1; color: var(--tu-ink); } span { font-size: 15px; font-weight: 600; color: var(--tu-ink-soft); } }
.ucd-score__track { position: relative; height: 10px; border-radius: 999px; background: var(--tu-blue-tint); }
.ucd-score__fill { height: 100%; border-radius: 999px; background: var(--tu-blue); transition: width 0.3s ease; }
.ucd-score__floor { position: absolute; top: -5px; width: 3px; height: 20px; margin-left: -1px; border-radius: 2px; background: var(--tu-ink); }
.ucd-score__note { margin: 12px 0 0; font-size: 12.5px; line-height: 1.45; color: var(--tu-ink-soft); }
.ucd-score__dot { display: inline-block; width: 3px; height: 11px; margin-right: 7px; vertical-align: -1px; border-radius: 2px; background: var(--tu-ink); }

.ucd-actions { display: flex; flex-direction: column; gap: 10px; }
.ucd-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; width: 100%; min-height: 50px; padding: 0 18px; border-radius: 12px; border: 1px solid transparent; font: 700 15px/1 var(--tu-font-text); cursor: pointer; text-decoration: none !important; transition: background 0.15s ease, border-color 0.15s ease; svg { width: 18px; height: 18px; } }
.ucd-btn--solid { background: var(--tu-ink); color: #fff !important; &:hover { background: var(--tu-blue); } &:disabled { background: var(--tu-line-soft); color: var(--tu-ink-mute) !important; cursor: not-allowed; } }
.ucd-btn--ghost { background: var(--tu-surface); border-color: var(--tu-line-strong); color: var(--tu-ink) !important; &:hover { border-color: var(--tu-blue); color: var(--tu-blue) !important; } &:disabled { opacity: 0.5; cursor: not-allowed; } }
.ucd-contacts { list-style: none; margin: 0; padding: 14px 16px; border-radius: 12px; background: var(--tu-blue-tint); display: flex; flex-direction: column; gap: 12px; li { display: flex; flex-direction: column; gap: 2px; font-size: 13.5px; b { color: var(--tu-ink); } span { color: var(--tu-ink-soft); } a { color: var(--tu-blue-deep); font-weight: 600; } } }
.ucd-dates { display: flex; flex-direction: column; gap: 12px; margin: 0; > div { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; } dt { margin: 0; font-size: 13px; font-weight: 600; color: var(--tu-ink-soft); } dd { margin: 0; font-size: 15px; font-weight: 700; color: var(--tu-ink); text-align: right; } }

/* Tabs: the public page's underline tabs, switching content instead of scrolling */
.ucd-tabs { display: flex; gap: 32px; border-bottom: 1px solid var(--tu-line-soft); margin: 0 0 20px; }
.ucd-tab { display: inline-flex; align-items: center; gap: 8px; appearance: none; border: 0; border-bottom: 3px solid transparent; background: transparent; min-height: 52px; padding: 0 2px; font: 600 17px/1 var(--tu-font-text); color: var(--tu-ink); cursor: pointer; transition: border-color 0.2s ease, color 0.2s ease; &:hover { color: var(--tu-blue); } &.is-active { border-bottom-color: var(--tu-blue); color: var(--tu-blue); } }
.ucd-count { display: inline-grid; place-items: center; min-width: 24px; height: 24px; padding: 0 8px; border-radius: 999px; background: var(--tu-blue-tint); color: var(--tu-blue-deep); font-size: 12.5px; font-weight: 700; }
.ucd-lead { margin: 0 0 14px; font-size: 15px; line-height: 1.55; color: var(--tu-ink-soft); }
.ucd-none { margin: 12px 0 0; font-size: 14px; color: var(--tu-ink-soft); }

/* Prereqs: the public page's chips, groups and items */
.ucd-filter { display: flex; flex-wrap: wrap; gap: 8px; margin: 0 0 6px; }
.ucd-seg { display: inline-flex; align-items: center; gap: 10px; min-height: 38px; padding: 0 8px 0 14px; border: 1px solid var(--tu-line); border-radius: 999px; background: var(--tu-surface); color: var(--tu-ink); font: 600 14px var(--tu-font-text); cursor: pointer; &:hover { border-color: var(--tu-line-strong); } &.is-on { border-color: var(--tu-blue); background: var(--tu-blue-tint); color: var(--tu-blue-deep); } }
.ucd-seg__n { display: inline-grid; place-items: center; min-width: 24px; height: 24px; padding: 0 7px; border-radius: 999px; background: var(--tu-surface-2); font-size: 12px; font-weight: 800; color: var(--tu-ink-soft); }
.ucd-seg.is-on .ucd-seg__n { background: var(--tu-blue); color: #fff; }
.tu-prq-group { margin-top: 10px; border: 1px solid var(--tu-line); border-radius: 16px; background: var(--tu-surface); padding: 0 16px; transition: border-color 0.15s ease; &:hover, &[open] { border-color: var(--tu-line-strong); } }
.tu-prq-group:first-of-type { margin-top: 14px; }
.tu-prq-group__sum { display: flex; align-items: center; gap: 12px; padding: 14px 0; list-style: none; cursor: pointer; &::-webkit-details-marker { display: none; } }
.tu-prq-group__ico { display: grid; place-items: center; flex: none; width: 40px; height: 40px; border-radius: 12px; background: var(--tu-blue-tint); color: var(--tu-blue-deep); ::v-deep .tu-ui-icon { display: inline-flex; } }
.tu-prq-group__title { margin: 0 !important; font-family: var(--tu-font-text) !important; font-size: 16px !important; font-weight: 700 !important; color: var(--tu-ink); }
.tu-prq-group__count { display: inline-grid; place-items: center; min-width: 26px; height: 24px; padding: 0 8px; border-radius: 999px; background: var(--tu-blue-tint); color: var(--tu-blue-deep); font-size: 12.5px; font-weight: 700; }
.tu-prq-group__chev { width: 18px; height: 18px; margin-left: auto; color: var(--tu-ink-mute); transition: transform 0.15s ease; }
.tu-prq-group[open] .tu-prq-group__chev { transform: rotate(180deg); }
.ucd-prq-grid { list-style: none; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin: 0; padding: 0 0 16px !important; }
.ucd-prq-item { display: flex; align-items: center; gap: 12px; width: 100%; box-sizing: border-box; min-height: 60px; padding: 10px 14px; border: 1px solid var(--tu-line); border-radius: 12px; background: var(--tu-surface); text-align: left; font: inherit; color: var(--tu-ink); cursor: pointer; &:hover { border-color: var(--tu-blue); background: var(--tu-blue-tint); } }
.ucd-prq-item__text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; b { font-size: 14.5px; font-weight: 700; line-height: 1.3; } small { font-size: 12.5px; color: var(--tu-ink-soft); } }
.ucd-prq-item__pts { flex: none; font-size: 13px; font-weight: 700; color: var(--tu-green); }
.ucd-prq-item__go { width: 16px; height: 16px; flex: none; color: var(--tu-ink-mute); }

/* Scorecard: one card per step; the resume steps fold into one */
.ucd-steps { display: flex; flex-direction: column; gap: 10px; }
.ucd-row--card, .ucd-group { border: 1px solid var(--tu-line); border-radius: 14px; background: var(--tu-surface); }
.ucd-row--card { padding: 14px 16px; }
.ucd-group { padding: 0 16px; &[open] { border-color: var(--tu-line-strong); } }
.ucd-group__sum { display: flex; align-items: center; gap: 12px; padding: 14px 0; list-style: none; cursor: pointer; &::-webkit-details-marker { display: none; } }
.ucd-group__ico { flex: none; color: var(--tu-blue); }
.ucd-group__copy { flex: 1; min-width: 0; strong { display: block; font-size: 15px; color: var(--tu-ink); } p { margin: 2px 0 0; font-size: 13px; line-height: 1.4; color: var(--tu-ink-soft); } }
.ucd-group__pts { flex: none; padding: 6px 12px; border-radius: 999px; background: var(--tu-surface-2); font-size: 13px; font-weight: 700; color: var(--tu-ink-soft); font-variant-numeric: tabular-nums; &.is-on { background: var(--tu-green-tint); color: var(--tu-green); } }
.ucd-group__chev { width: 18px; height: 18px; flex: none; color: var(--tu-ink-mute); transition: transform 0.15s ease; }
.ucd-group[open] .ucd-group__chev { transform: rotate(180deg); }
.ucd-group__body { display: flex; flex-direction: column; padding: 0 0 6px; }
.ucd-group__body .ucd-row { padding: 12px 0; border-top: 1px solid var(--tu-line-soft); }

@media (max-width: 960px) {
  .ucd-layout { grid-template-columns: 1fr; gap: 20px; }
  .ucd-rail { position: static; }
  .ucd-prq-grid { grid-template-columns: 1fr; }
  .ucd-tabs { gap: 24px; }
  .ucd-tab { min-height: 46px; font-size: 15px; }
}
</style>
